import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { App } from "antd";
import { TFunction } from "i18next/typescript/t";

import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormTextArea } from "@/components_v2/FormFields/ReactHookForm/fields";
import { useRejectApplicationByIdMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import {
  ApplicationRejectModalProps,
  RejectApplicationFormValues,
} from "@/features/organizations/profile/types";

import "../styles.css";

const rejectApplicationSchema = (
  t: TFunction,
): yup.ObjectSchema<RejectApplicationFormValues> =>
  yup.object().shape({
    rejectionReason: yup
      .string()
      .typeError(t("organizations.validation.notValid"))
      .required(t("organizations.validation.required")),
  });

const ApplicationRejectModal = ({
  open,
  setOpen,
  application,
  onRejected,
}: ApplicationRejectModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [rejectApplicationById] = useRejectApplicationByIdMutation();

  const form = useForm<RejectApplicationFormValues>({
    resolver: yupResolver(rejectApplicationSchema(t)),
    mode: "onChange",
    defaultValues: {
      rejectionReason: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
    }
  }, [open]);

  const onClose = () => setOpen(false);

  const handleSubmit = (values: RejectApplicationFormValues) => {
    rejectApplicationById({ id: application.id, data: values })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.applicationRejected"),
        });
        onClose();
        onRejected();
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <BaseModal
      destroyOnClose
      title={t("organizations.labels.rejectApplication")}
      open={open}
      onCancel={onClose}
      footer={
        <div className="modal-footer-buttons">
          <BaseButton
            size={BUTTON_SIZES.SMALL}
            variant={BUTTON_VARIANTS.TERTIARY}
            onClick={onClose}
          >
            {t("organizations.labels.cancel")}
          </BaseButton>

          <BaseButton
            variant={BUTTON_VARIANTS.DANGER}
            size={BUTTON_SIZES.SMALL}
            loading={form.formState.isSubmitting}
            onClick={form.handleSubmit(handleSubmit)}
          >
            {t("organizations.labels.rejectApplication")}
          </BaseButton>
        </div>
      }
    >
      <div className="form-input-container">
        <FormFieldWrapper
          label={t("organizations.labels.rejectionReason")}
          required
        >
          <HookFormTextArea
            name="rejectionReason"
            placeholder={t("organizations.placeholders.enterRejectionReason")}
            control={form.control}
          />
        </FormFieldWrapper>
      </div>
    </BaseModal>
  );
};

export default ApplicationRejectModal;
